import { useEffect, useRef } from "react";
import errorToast from "./error-toast";
import FileSaver from "file-saver";
import { purchaseProductListAction } from "../../redux/actions/purchase-action";
import successToast from "./success-toast";
import { transactionPrintAction } from "../../redux/actions/general-action";
import { useDispatch } from "react-redux";
import { printDocument } from "./printing/print-service";

export function formatString(str) {
  const words = str
    ?.split("_")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
  return words?.join(" ");
}

// const useBarcodeScanner = (onBarcodeScanned) => {
//   useEffect(() => {
//     let barcodeBuffer = ""; // Buffer to accumulate scanned characters
//     let lastKeyPressTime = 0; // Timestamp of the last key press
//     let isScanning = false; // Flag to indicate whether we are scanning

//     const handleBarcodeScan = (event) => {
//       const currentTime = new Date().getTime();
//       const scannedCharacter = event.key;
//

//       if (
//         scannedCharacter === "Shift" ||
//         event.ctrlKey ||
//         event.altKey ||
//         event.metaKey ||
//         scannedCharacter === "Enter"
//       ) {
//         // Ignore special keys like Shift, Ctrl, Alt, Meta (Command on Mac), and Enter key
//         return;
//       }

//       if (currentTime - lastKeyPressTime > 50) {
//         // If the time between key presses is greater than 50 milliseconds, assume it's scanner input
//         barcodeBuffer = scannedCharacter; // Start a new barcode sequence
//         isScanning = true; // Set scanning flag to true
//       } else {
//         barcodeBuffer += scannedCharacter; // Continue accumulating characters
//       }

//       lastKeyPressTime = currentTime; // Update the timestamp
//     };

//     document.addEventListener("keydown", handleBarcodeScan);

//     const handleEnterKey = (event) => {
//       if (event.key === "Enter" && isScanning) {
//         event.preventDefault(); // Prevent the default behavior of the Enter key
//         onBarcodeScanned(barcodeBuffer); // Call the callback when Enter key is pressed
//         barcodeBuffer = ""; // Clear the buffer
//         isScanning = false; // Reset scanning flag
//       }
//     };

//     document.addEventListener("keydown", handleEnterKey);

//     return () => {
//       document.removeEventListener("keydown", handleBarcodeScan);
//       document.removeEventListener("keydown", handleEnterKey);
//     };
//   }, [onBarcodeScanned]);
// };

// export default useBarcodeScanner;

// const useBarcodeScanner = (onBarcodeScanned) => {
//   useEffect(() => {
//     let barcodeBuffer = "";
//     let lastKeyPressTime = 0;
//     let isScanning = false;
//     const expectedBarcodeLength = 2;
//     const barcodeScanThreshold = 1000; // Adjust as needed

//     const handleBarcodeScan = (event) => {

//       const currentTime = new Date().getTime();
//       const scannedCharacter = event.key;

//       if (
//         scannedCharacter === "Shift" ||
//         event.ctrlKey ||
//         event.altKey ||
//         event.metaKey ||
//         scannedCharacter === "Enter"
//       ) {
//         return;
//       }

//       if (currentTime - lastKeyPressTime < barcodeScanThreshold) {
//         barcodeBuffer = scannedCharacter;
//         isScanning = true;
//       } else {
//         barcodeBuffer += scannedCharacter;
//       }
//       lastKeyPressTime = currentTime;
//     };
//     const handleEnterKey = (event) => {
//       if (event.key === "Enter" && isScanning) {
//         event.preventDefault();
//         if (barcodeBuffer.length >= expectedBarcodeLength) {
//           onBarcodeScanned(barcodeBuffer);
//         }
//         barcodeBuffer = "";
//         isScanning = false;
//       }
//     };

//     document.addEventListener("keydown", handleBarcodeScan);
//     document.addEventListener("keydown", handleEnterKey);

//     return () => {
//       document.removeEventListener("keydown", handleBarcodeScan);
//       document.removeEventListener("keydown", handleEnterKey);
//     };
//   }, [onBarcodeScanned]);
// };

// export default useBarcodeScanner;

// Barcode scanner input. A scanner "types" the barcode a few ms per character
// and ends it with Enter. Whatever field has focus receives those keys, so
// once a burst turns out to be a scan, that field is put back to the value it
// had before the burst (through React's change event, so form state follows)
// and the scanner's Enter is swallowed before the field's own handlers run.
// One document listener is shared by every mounted useBarcodeScanner.
const SCAN_CHAR_GAP_MS = 50; // max gap between two scanned characters
const SCAN_MIN_LENGTH = 4; // a person can't type this many keys this fast
const barcodeSubscribers = new Set();
const scanState = { buffer: "", lastKeyAt: 0, target: null, targetValue: "" };

const isTextField = (el) =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

// Set a React-controlled field's value so its onChange fires
const restoreFieldValue = (el, value) => {
  if (!isTextField(el) || el.value === value) return;
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
};

const handleScannerKeyDown = (event) => {
  if (barcodeSubscribers.size === 0) return;
  const now = event.timeStamp;
  const isFast = now - scanState.lastKeyAt < SCAN_CHAR_GAP_MS;

  if (event.key === "Enter") {
    const isScan =
      scanState.buffer.length >= SCAN_MIN_LENGTH &&
      now - scanState.lastKeyAt < SCAN_CHAR_GAP_MS * 2;
    if (isScan) {
      event.preventDefault();
      event.stopPropagation();
      const barcode = scanState.buffer;
      restoreFieldValue(scanState.target, scanState.targetValue);
      barcodeSubscribers.forEach((subscriber) => subscriber.current?.(barcode));
    }
    scanState.buffer = "";
    scanState.lastKeyAt = 0;
    return;
  }

  if (
    !event.key ||
    event.key.length !== 1 ||
    event.ctrlKey ||
    event.altKey ||
    event.metaKey
  ) {
    return;
  }

  if (isFast && scanState.buffer) {
    scanState.buffer += event.key;
  } else {
    // A new burst — remember the focused field as it is before this key lands
    scanState.buffer = event.key;
    scanState.target = document.activeElement;
    scanState.targetValue = isTextField(scanState.target)
      ? scanState.target.value
      : "";
  }
  scanState.lastKeyAt = now;
};

const useBarcodeScanner = (onBarcodeScanned) => {
  const subscriberRef = useRef(onBarcodeScanned);
  subscriberRef.current = onBarcodeScanned;

  useEffect(() => {
    if (barcodeSubscribers.size === 0) {
      // capture phase, so it runs before the focused field's handlers
      window.addEventListener("keydown", handleScannerKeyDown, true);
    }
    barcodeSubscribers.add(subscriberRef);
    return () => {
      barcodeSubscribers.delete(subscriberRef);
      if (barcodeSubscribers.size === 0) {
        window.removeEventListener("keydown", handleScannerKeyDown, true);
      }
    };
  }, []);
};

export default useBarcodeScanner;

export const formatYAxisTick = (tick) => {
  if (tick >= 100000) {
    return `${(tick / 100000).toFixed(0)}L`;
  } else if (tick >= 1000) {
    return `${(tick / 1000).toFixed(0)}K`;
  } else {
    return tick;
  }
};

export const handleShowErrorToast = (errors) => {
  const getFirstError = (obj) => {
    if (typeof obj === "string") return obj;
    if (Array.isArray(obj)) {
      for (let item of obj) {
        const error = getFirstError(item);
        if (error) return error;
      }
    }
    if (typeof obj === "object" && obj !== null) {
      for (let key in obj) {
        const error = getFirstError(obj[key]);
        if (error) return error;
      }
    }
    return null;
  };

  const firstError = getFirstError(errors);
  if (firstError) {
    errorToast(firstError + " !");
  }
};

export const hexToRgba = (hex, opacity) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const downloadAllTemplates = (documentsArray) => {
  documentsArray.forEach(({ documentType, expectedHeaders }) => {
    const headers = expectedHeaders.join(",") + "\n";
    const csvContent = headers;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, `${documentType}_Template.csv`);
  });
};

export const downloadAllTemplatesJson = (documentsArray) => {
  documentsArray.forEach(({ documentType, expectedHeaders }) => {
    const sampleData = [
      Object.fromEntries(expectedHeaders.map((h) => [h, ""])),
    ];
    const jsonContent = JSON.stringify(sampleData, null, 2);
    const blob = new Blob([jsonContent], {
      type: "application/json;charset=utf-8;",
    });
    FileSaver.saveAs(blob, `${documentType}_Template.json`);
  });
};

export const fetchData = async (
  inputValue,
  callback,
  field,
  values,
  dispatch
) => {
  const onSuccess = (data) => {
    const options =
      data?.data === null
        ? [
          {
            value: { text: "new_item", itemText: inputValue },
            label: <span className="text-primary">+ New Item</span>,
          },
        ]
        : [
          ...data.data.map((item) => ({
            value: item.item_id,
            label: item[field],
            ...item,
          })),
        ];
    callback(options);
  };

  const onError = (error) => {
    callback([
      {
        value: { text: "new_item", itemText: inputValue },
        label: <span className="text-primary">+ New Item</span>,
      },
    ]);
  };
  if (inputValue?.length > 0) {
    dispatch(
      purchaseProductListAction(
        {
          field: field,
          value: inputValue,
        },
        onSuccess,
        onError
      )
    );
  }
};

export const getBackgroundColor = (isActive, color = "--primary") => {
  const primaryHex = getComputedStyle(document.documentElement)
    .getPropertyValue(color)
    .trim();
  return isActive ? hexToRgba(primaryHex, 0.15) : "";
};

// export const preprocessData = (data) => {
//   return data.map((item) => {
//     const processedItem = {};

//     for (const key in item) {
//       let processedValue = item[key] === null ? "--" : item[key];
//       if (
//         typeof processedValue === "string" &&
//         processedValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) &&
//         !isNaN(Date.parse(processedValue))
//       ) {
//         processedValue = new Date(processedValue).toLocaleString();
//       }
//       const formattedKey = key.replace(/_/g, " ").toUpperCase();
//       processedItem[formattedKey] = processedValue;
//     }
//     return processedItem;
//   });
// };
export const preprocessData = (data) => {
  return data.map((item) => {
    const processedItem = {};

    for (const key in item) {
      let processedValue = item[key] === null ? "--" : item[key];
      if (
        typeof processedValue === "string" &&
        processedValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) &&
        !isNaN(Date.parse(processedValue))
      ) {
        processedValue = new Date(processedValue).toLocaleString();
      }
      // Simply keep the original key without formatting
      processedItem[key] = processedValue;
    }
    return processedItem;
  });
};

export const transaction_type = [
  { value: "Billing", label: "Billing" },
  { value: "invoice_credit_note", label: "Invoice Credit Note" },
];

export const positiveIntegerFn = (event) => {
  if (event.key === "-" || event.key === "e" || event.key === ".") {
    event.preventDefault();
  }
};

export const decimalNumberFn = (event) => {
  // Allow keyboard shortcuts (Ctrl/Cmd/Alt combos like select-all, copy, paste)
  // and non-character keys (Backspace, Delete, Tab, Enter, arrows) — their
  // event.key is multi-character, so they must pass through untouched.
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (event.key.length > 1) return;

  const inputValue = event.target.value ?? "";

  // Allow only digits and a single decimal point. This blocks letters and signs
  // (e.g. "11.sa2", "1e3", "-1"), so the field accepts values like 11 or 11.55.
  if (!/[0-9.]/.test(event.key)) {
    event.preventDefault();
    return;
  }
  // Block a second decimal point.
  if (event.key === "." && inputValue.includes(".")) {
    event.preventDefault();
  }
};

export const capitalizeWords = (str) => {
  return str?.toLowerCase()?.replace(/\b\w/g, (c) => c.toUpperCase());
};

export const parseStringToNumber = (value) => {
  if (typeof value === "string") {
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? value : parsedValue; // Return the parsed number if valid, otherwise return the original value
  }
  return value;
};

export function truncateString(str, length) {
  if (str.length > length) {
    return str.slice(0, length) + "...";
  }
  return str;
}

export const escapeHtml = (value) => {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[char]));
};

// options.widthMm/heightMm print on a custom page size (e.g. thermal labels)
// instead of the default A4.
export const handleElectronPrint = (htmlString, options) => {
  if (window.electron && window.electron.ipcRenderer) {
    window.electron.ipcRenderer.send("print-html", htmlString, options);
    successToast("Print Successful");
  } else {
  }
};
// export const displayHtmlContent = (htmlContent) => {

//   console.log(htmlContent, "htmlContent");

//   const newWindow = window.open("", "_blank");
//   newWindow.document.write(htmlContent);
//   newWindow.document.close();
//   newWindow.print();
// };

export const displayHtmlContent = (htmlContent) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "absolute";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(htmlContent);
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      document.body.removeChild(iframe);
    }, 300);
  };
};

// Prints once: silently through the desktop (Electron) app when running in
// it, otherwise through the browser print dialog. Calling both used to
// print twice in the desktop app.
export const printHtml = (htmlContent, electronOptions) => {
  if (window.electron && window.electron.ipcRenderer) {
    handleElectronPrint(htmlContent, electronOptions);
  } else {
    displayHtmlContent(htmlContent);
  }
};

// print_preferences type for each transaction_type used by the print buttons
const PRINT_DOC_TYPE = {
  Billing: "billing_invoice",
  invoice_credit_note: "billing_return",
  sales_order: "sales_order",
  sales_delivery: "delivery_note",
  sales_invoice: "sales_invoice",
  sales_credit_note: "sales_credit_note",
  sales_return: "sales_return",
  purchase_order: "purchase_order",
  goods_receipt_note: "good_receipt_note",
  purchase_invoice: "purchase_invoice",
  purchase_credit_note: "purchase_credit_note",
  purchase_return: "purchase_return",
  stock_transfer: "stock_transfer",
};

// Prints a document with the printer, paper size, orientation and copies from
// Print Preferences and Settings > Printer Setup (see printing/print-service.js).
// For "Billing", id can also be an array of sot_ids, printed as one invoice.
export function handlea4Print(id, type, dispatch) {
  dispatch(
    transactionPrintAction(
      { transaction_id: id, transaction_type: type },
      (response) => {
        printDocument(response.data?.[0], { docType: PRINT_DOC_TYPE[type] });
      },
      (response) => {
        errorToast(response?.data?.message);
      }
    )
  );
}

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
export const minDate = tomorrow.toISOString().split("T")[0];

export function formatIndianNumber(num) {
  if (num >= 10000000) {
    // 1 Crore or more
    return `${(num / 10000000).toFixed(2)} Crore`;
  } else if (num >= 100000) {
    // 1 Lakh or more
    return `${(num / 100000).toFixed(2)} Lakh`;
  } else {
    return num.toFixed(2); // You can format this too if needed
  }
}

// Examples:
